import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog'
import { Input } from '../ui/input'
import { Label } from '../ui/label'

export type OrganizationAction = 'suspend' | 'unsuspend' | 'delete' | 'restore'

interface Props {
    action: OrganizationAction | null
    orgCode: string
    onCancel: () => void
    onConfirm: () => void
    pending?: boolean
}

// Destructive actions (delete) require the admin to type the org code to
// arm the confirm button. Non-destructive actions (suspend / unsuspend /
// restore) show the dialog but do not gate the button on a typed code.
function isTypeToConfirm(action: OrganizationAction | null): boolean {
    return action === 'delete'
}

export function OrganizationActionDialog({ action, orgCode, onCancel, onConfirm, pending }: Props) {
    const { t } = useTranslation('admin')
    const [typed, setTyped] = useState('')

    useEffect(() => {
        setTyped('')
    }, [action, orgCode])

    const open = Boolean(action)
    const typeGate = isTypeToConfirm(action)
    const armed = !typeGate || typed === orgCode

    const title = action ? t(`organizations.confirm.title_${action}`) : ''
    const body = action ? t(`organizations.confirm.body_${action}`, { code: orgCode }) : ''

    return (
        <AlertDialog open={open} onOpenChange={(v) => { if (!v) onCancel() }}>
            <AlertDialogContent data-testid="org-action-dialog">
                <AlertDialogHeader>
                    <AlertDialogTitle>{title}</AlertDialogTitle>
                    <AlertDialogDescription>{body}</AlertDialogDescription>
                </AlertDialogHeader>

                {typeGate && (
                    <div className="space-y-2">
                        <Label htmlFor="org-action-confirm-code">
                            {t('organizations.confirm.confirm_label')}
                        </Label>
                        <Input
                            id="org-action-confirm-code"
                            value={typed}
                            onChange={(e) => setTyped(e.target.value)}
                            placeholder={orgCode}
                            autoFocus
                            data-testid="org-action-confirm-input"
                        />
                    </div>
                )}

                <AlertDialogFooter>
                    <AlertDialogCancel onClick={onCancel}>
                        {t('organizations.confirm.cancel')}
                    </AlertDialogCancel>
                    <AlertDialogAction
                        onClick={onConfirm}
                        disabled={!armed || pending}
                        className={action === 'delete' ? 'bg-destructive hover:bg-destructive/90' : ''}
                        data-testid="org-action-confirm-button"
                    >
                        {t('organizations.confirm.proceed')}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    )
}
